import type { SortDirection, SortOrder } from '@brightdrop/table-ui';
import { AssetReportType } from '~/common/models/asset.model';

export const DEFAULT_SORT_ORDER: SortOrder = {
  name: 'name',
  direction: 'asc',
};

/** Tabs that support server-side column sorting. */
export const SORTABLE_TABS = [
  AssetReportType.FLEET_READINESS,
  AssetReportType.DIAGNOSTICS,
  AssetReportType.BATTERY,
] as const;

/**
 * Single source of truth for sortable columns and per-column behavior.
 * Key MUST match API field name for the column.
 */
export type SortColumnConfig = {
  /** If true, UI direction is reversed when sent to API (e.g. "days ago" fields). */
  reverseDirectionForApi?: boolean;
  /**
   * Optional: restrict column to specific tabs. If omitted, allowed on all sortable tabs.
   */
  allowedTabs?: ReadonlyArray<(typeof SORTABLE_TABS)[number]>;
};

export const SORT_COLUMN_CONFIG = {
  name: {},
  licensePlate: {},
  lastCommunicated: { reverseDirectionForApi: true },
  lastIgnition: { reverseDirectionForApi: true },
  estimatedRange: {},
  lastOnboardedDate: {},
} as const satisfies Record<string, SortColumnConfig>;

/** Column names that support sorting (derived from SORT_COLUMN_CONFIG). */
export const ALLOWED_SORT_COLUMNS: readonly string[] =
  Object.keys(SORT_COLUMN_CONFIG);

/**
 * Returns the sort direction to send to the API. For columns with reverseDirectionForApi (e.g. lastCommunicated),
 * reverses the UI direction so that "days ago" ordering matches.
 */
export const getApiSortDirection = (
  columnName: string,
  uiDirection: SortDirection
): SortDirection => {
  const config = (SORT_COLUMN_CONFIG as Record<string, SortColumnConfig>)[
    columnName
  ];
  return config?.reverseDirectionForApi
    ? uiDirection === 'asc'
      ? 'desc'
      : 'asc'
    : uiDirection;
};

const VEHICLE_LIST_SORT_STORAGE_PREFIX = 'vehicleListSort';

/**
 * Builds the localStorage key for vehicle list sort (per tab only).
 * Tab-only key is stable on refresh; sessionId can be "all" before context loads, which would otherwise miss stored sort.
 */
export const getVehicleListSortStorageKey = (tab: string): string =>
  `${VEHICLE_LIST_SORT_STORAGE_PREFIX}-${tab}`;

/**
 * Parses and validates a stored sort order string (from useLocalStorage getItem).
 * @returns Parsed sort order or null if missing/invalid
 */
export const parseStoredSortOrder = (raw: string | null): SortOrder | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SortOrder;
    if (
      typeof parsed?.name === 'string' &&
      (parsed.direction === 'asc' || parsed.direction === 'desc') &&
      parsed.name in SORT_COLUMN_CONFIG
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Reads persisted sort order directly from localStorage (same key/format as useLocalStorage).
 * Use as fallback on refresh when the provider cache may not be hydrated yet.
 */
export const getStoredSortOrderFromLocalStorage = (
  key: string
): SortOrder | null => {
  try {
    const raw =
      typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    if (!raw) return null;
    const item = JSON.parse(raw) as { value?: string; expiry?: number };
    if (item?.expiry && Date.now() > item.expiry) return null;
    const value = item?.value;
    return typeof value === 'string' ? parseStoredSortOrder(value) : null;
  } catch {
    return null;
  }
};

/**
 * Formats sort order for overview API (e.g. "name asc", "licensePlate desc").
 * Uses getApiSortDirection so lastCommunicated sends reversed direction to API while UI shows asc/desc.
 */
export const formatSortForOverview = (
  sortOrder: SortOrder | undefined
): string | undefined => {
  if (!sortOrder?.name || !(sortOrder.name in SORT_COLUMN_CONFIG)) {
    return undefined;
  }
  const direction = getApiSortDirection(
    sortOrder.name,
    sortOrder.direction ?? DEFAULT_SORT_ORDER.direction
  );
  return `${sortOrder.name} ${direction}`;
};
